import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const StoreCtx = createContext(null);
export const lineKey = (pid, cut) => `${pid}|${cut || ""}`;
export const lineTid = (i) => (i.cut ? `${i.product_id}-${i.cut.toLowerCase().replace(/\W+/g, "-")}` : i.product_id);
const load = (k, d) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; }
};

export function StoreProvider({ children }) {
  const [area, setAreaState] = useState(() => load("vf_area", null));
  const [cart, setCart] = useState(() => load("vf_cart", []).map((i) => ({ ...i, cut: i.cut || "", key: lineKey(i.product_id, i.cut) })));
  const [customer, setCustomer] = useState(() => load("vf_customer", null));
  const [config, setConfig] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);

  useEffect(() => { localStorage.setItem("vf_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const saved = load("vf_area", null);
    if (!saved) return;
    api.get("/areas/check", { params: { pincode: saved.pincode } }).then((r) => {
      const a = r.data.serviceable ? r.data.area : null;
      setAreaState(a);
      localStorage.setItem("vf_area", JSON.stringify(a));
    }).catch(() => {});
  }, []);

  const setArea = (a) => { setAreaState(a); localStorage.setItem("vf_area", JSON.stringify(a)); };

  const add = (p, cut = "") => setCart((c) => {
    const key = lineKey(p.id, cut);
    const ex = c.find((i) => i.key === key);
    if (ex) return c.map((i) => (i.key === key ? { ...i, qty: Math.min(i.qty + 1, p.available_qty) } : i));
    const charge = cut ? (p.cut_options || []).find((o) => o.label === cut)?.charge || 0 : 0;
    return [...c, { key, product_id: p.id, cut, name: p.name, unit: p.unit, price: p.price + charge, image: p.image, qty: 1, available_qty: p.available_qty, needs_prep: !!(p.needs_prep || cut) }];
  });
  const setQty = (key, qty) => setCart((c) => (qty <= 0 ? c.filter((i) => i.key !== key) : c.map((i) => (i.key === key ? { ...i, qty: Math.min(qty, i.available_qty) } : i))));
  const clear = () => setCart([]);

  const applyServerItems = useCallback((items) => {
    const notes = [];
    const next = [];
    items.forEach((it) => {
      if (it.removed || it.available_qty <= 0) { if (it.name) notes.push(`${it.name} is now unavailable and was removed`); return; }
      const label = it.cut ? `${it.name} (${it.cut})` : it.name;
      if (it.price_changed) notes.push(`${label}: price changed ₹${it.old_price} → ₹${it.price}`);
      const qty = Math.min(it.qty, it.available_qty);
      if (it.qty_short) notes.push(`${label}: only ${it.available_qty} left, quantity adjusted`);
      next.push({ key: lineKey(it.product_id, it.cut), product_id: it.product_id, cut: it.cut || "", needs_prep: !!it.needs_prep, name: it.name, unit: it.unit, price: it.price, image: it.image, qty, available_qty: it.available_qty });
    });
    setCart(next);
    notes.forEach((n) => toast.warning(n));
    return notes;
  }, []);

  const refreshCart = useCallback(async () => {
    const current = load("vf_cart", []);
    if (!current.length) return [];
    const r = await api.post("/cart/validate", { items: current.map((i) => ({ product_id: i.product_id, qty: i.qty, price: i.price, cut: i.cut || "" })) });
    return r.data.changed ? applyServerItems(r.data.items) : [];
  }, [applyServerItems]);

  const login = (token, cust) => {
    localStorage.setItem("vf_token", token);
    localStorage.setItem("vf_customer", JSON.stringify(cust));
    setCustomer(cust);
  };
  const logout = () => {
    api.post("/customer/logout").catch(() => {});
    localStorage.removeItem("vf_token");
    localStorage.removeItem("vf_customer");
    setCustomer(null);
  };

  const count = cart.reduce((s, i) => s + i.qty, 0);
  const subtotal = cart.reduce((s, i) => s + i.qty * i.price, 0);
  const deliveryFee = area?.delivery_fee || 0;
  const minOrder = area?.min_order || 0;
  const shortBy = Math.max(0, minOrder - subtotal);
  const total = subtotal + deliveryFee;

  return (
    <StoreCtx.Provider value={{ area, setArea, cart, add, setQty, clear, count, subtotal, deliveryFee, minOrder, shortBy, total, refreshCart, applyServerItems,
      customer, login, logout, config, cartOpen, setCartOpen, areaOpen, setAreaOpen }}>
      {children}
    </StoreCtx.Provider>
  );
}

export const useStore = () => useContext(StoreCtx);
